/**
 * Transport abstraction so room logic is testable without a network.
 * `relayTransport.ts` implements it over the Cloudflare relay Worker.
 */

export interface Conn {
  /** Remote id: the guest's clientId on the host side, 'host' on the guest side. */
  readonly peerId: string;
  send(data: unknown): void;
  close(): void;
  onData(cb: (data: unknown) => void): void;
  onClose(cb: () => void): void;
  /**
   * Guest side: the host attached to / detached from the relay. Fires right
   * away with the current value once known.
   */
  onHostPresence?(cb: (present: boolean) => void): void;
}

export type TransportErrorKind =
  'id-taken' | 'peer-unavailable' | 'ended' | 'network' | 'unknown';

export class TransportError extends Error {
  readonly kind: TransportErrorKind;
  constructor(kind: TransportErrorKind, message: string) {
    super(message);
    this.kind = kind;
    this.name = 'TransportError';
  }
}

export type LinkStatus = 'connected' | 'reconnecting';

/** Diagnostics events the transport reports (best-effort, for the Connection sheet). */
export type TransportEvent =
  | { type: 'attached'; epoch: number }
  | { type: 'inbound' }
  | { type: 'outbound' }
  | { type: 'pong'; rttMs: number }
  | { type: 'reconnect' }
  | { type: 'error'; message: string };

export interface Transport {
  /**
   * Host: attach to the room. Resolves once attached, with the host token
   * (a fresh room mints one; reopening requires the stored one). The link
   * reconnects by itself afterwards; guests are reconciled on re-attach.
   */
  listen(
    code: string,
    onConnection: (conn: Conn) => void,
    token?: string | null
  ): Promise<{ token: string }>;
  /** Guest: attach and return the single connection to the host. */
  connect(code: string): Promise<Conn>;
  /** Health of this device's link (host link only reconnects internally). */
  onStatus?(cb: (status: LinkStatus) => void): void;
  /** Diagnostics stream. */
  onEvent?(cb: (event: TransportEvent) => void): void;
  /** Check the current socket right now (after the tab wakes); reconnect if dead. */
  probe?(): void;
  /** Host: end the room for good on the relay. */
  end?(): void;
  destroy(): void;
}
